import { Inject, Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, catchError, firstValueFrom, from, map, of, switchMap, tap } from 'rxjs';
import { PriceCategory } from '../types/price-category';
import { Price, PricesList } from '../types/price';
import { MarketArea } from '../types/market-area';
import { IndexedDBService } from './indexeddb.service';
import { TimeSeries, TimeSeriesList } from '../types/time-series';
import { FEEDER_TYPES } from '../types/menu';
import { PRICE_TYPE_MAP, PRODUCT_MAP } from '../app.constants';

@Injectable()
export class MenuService {
  menu$ = new BehaviorSubject<any>(null);
  menuReady$ = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(HttpClient) private httpClient: HttpClient,
    @Inject(IndexedDBService) private indexedDBService: IndexedDBService,
    @Inject(NgZone) private ngZone: NgZone
  ) {}

  async loadMenu(forceReload: boolean = false) {
    this.ngZone.run(() => {
      this.menuReady$.next(false);
    });
    await this.indexedDBService.initDatabase();
    if (forceReload) {
      await this.indexedDBService.clear('historicPrices');
      await this.indexedDBService.clear('livePrices');
      await this.indexedDBService.clear('timeSeries');
      await this.indexedDBService.clear('priceCategories');
      await this.indexedDBService.clear('marketAreas');
    }
    const priceCategories = await firstValueFrom(this.loadPriceCategories(forceReload));
    const marketAreas = await firstValueFrom(this.loadMarketAreas(forceReload));
    const historicPrices = await firstValueFrom(this.loadHistoric(forceReload));
    const livePrices = await firstValueFrom(this.loadLive(forceReload));
    const timeSeries = await firstValueFrom(this.loadTimeSeries(forceReload));

    const categoryByName = new Map(priceCategories.map((pc) => [pc.name, pc]));
    const marketAreaById = new Map(marketAreas.map((ma) => [ma.id, ma]));
    const spotTypes = new Set(['Auction', 'Spot', 'Intraday', 'Imbalance', 'Forecast']);

    const menu: any = {};
    for (const key of this.getBaseMenu()) {
      menu[key] = {};
    }

    for (const price of historicPrices) {
      const { product, price_type, exchange } = price;
      const priceType = PRICE_TYPE_MAP[price_type] ?? price_type;
      const priceCategory = categoryByName.get(price.category);
      const marketArea = marketAreaById.get(priceCategory?.market_area_id ?? -1);
      const commodity = marketArea?.commodity;
      if (!commodity || !marketArea) continue;
      if (spotTypes.has(priceType)) {
        this.ensureArrayAtPath(menu, [FEEDER_TYPES.SPOT, commodity, marketArea.name, priceType]).push(price);
      } else if (priceType === 'HPFC' && price.product_code.includes('ICIS')) {
        this.ensureArrayAtPath(menu, [FEEDER_TYPES.ICIS_LONG_TERM_MODEL, 'Prices', commodity, marketArea.name]).push(
          price
        );
      } else if (['HPFC', 'MFC'].includes(priceType)) {
        this.ensureArrayAtPath(menu, [FEEDER_TYPES.FORWARD_CURVE, commodity, marketArea.name]).push(price);
      } else {
        this.ensureArrayAtPath(menu, [
          FEEDER_TYPES.FUTURES,
          commodity,
          marketArea.name,
          priceType,
          exchange,
          PRODUCT_MAP[product] ?? product,
        ]).push(price);
      }
    }
    for (const price of livePrices) {
      const { product, price_type, exchange } = price;
      const priceType = PRICE_TYPE_MAP[price_type] ?? price_type;
      const priceCategory = categoryByName.get(price.category);
      const marketArea = marketAreaById.get(priceCategory?.market_area_id ?? -1);
      const commodity = marketArea?.commodity;
      if (!commodity || !marketArea) continue;
      this.ensureArrayAtPath(menu, [
        FEEDER_TYPES.LIVE_PRICES,
        commodity,
        marketArea.name,
        priceType,
        exchange,
        PRODUCT_MAP[product] ?? product,
      ]).push(price);
    }
    for (const ts of timeSeries) {
      const { category, type, location } = ts;
      if (category && type && location) {
        if (ts.product_code.includes('ICIS')) {
          this.ensureArrayAtPath(menu, [FEEDER_TYPES.ICIS_LONG_TERM_MODEL, category, type, location]).push(ts);
        } else {
          this.ensureArrayAtPath(menu, [FEEDER_TYPES.FUNDAMENTAL_DATA, category, type, location]).push(ts);
        }
      }
    }
    this.ngZone.run(() => {
      this.menu$.next(menu);
      this.menuReady$.next(true);
    });
  }

  private ensureArrayAtPath(obj: any, keys: Array<string | number>): any[] {
    let current = obj;
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const isLast = i === keys.length - 1;
      if (isLast) {
        if (!Array.isArray(current[key])) {
          current[key] = [];
        }
        return current[key];
      } else {
        if (current[key] == null || typeof current[key] !== 'object' || Array.isArray(current[key])) {
          current[key] = {};
        }
        current = current[key];
      }
    }
    return [];
  }

  private getBaseMenu() {
    return [
      FEEDER_TYPES.FUTURES,
      FEEDER_TYPES.LIVE_PRICES,
      FEEDER_TYPES.FUNDAMENTAL_DATA,
      FEEDER_TYPES.FORWARD_CURVE,
      FEEDER_TYPES.ICIS_LONG_TERM_MODEL,
      FEEDER_TYPES.SPOT,
    ];
  }

  loadHistoricPrices() {
    const params = new HttpParams().append('data_type_filter', 'energy prices history').append('active', false);
    return this.httpClient.get<PricesList>(window.api_url + '/prices/list', { params }).pipe(
      map((pricesList) => pricesList.prices),
      tap((prices) => {
        this.indexedDBService.saveMany('historicPrices', prices);
      })
    );
  }
  loadLivePrices() {
    const params = new HttpParams().append('data_type_filter', 'energy prices live');
    return this.httpClient.get<PricesList>(window.api_url + '/prices/list', { params }).pipe(
      map((pricesList) => pricesList.prices),
      tap((prices) => {
        this.indexedDBService.saveMany('livePrices', prices);
      })
    );
  }
  loadTimeSeriesData() {
    return this.httpClient.get<TimeSeriesList>(window.api_url + '/time_series/list').pipe(
      map((timeSeriesList) => timeSeriesList.time_series),
      tap((timeSeries) => {
        this.indexedDBService.saveMany('timeSeries', timeSeries);
      })
    );
  }

  loadHistoric(forceReload: boolean = false) {
    if (forceReload) {
      return this.loadHistoricPrices();
    }
    return from(this.indexedDBService.getAll<Price>('historicPrices')).pipe(
      switchMap((prices) => {
        if (!prices.length) {
          return this.loadHistoricPrices();
        }
        return of(prices);
      })
    );
  }
  loadLive(forceReload: boolean = false) {
    if (forceReload) {
      return this.loadLivePrices();
    }
    return from(this.indexedDBService.getAll<Price>('livePrices')).pipe(
      switchMap((prices) => {
        if (!prices.length) {
          return this.loadLivePrices();
        }
        return of(prices);
      })
    );
  }
  loadTimeSeries(forceReload: boolean = false) {
    if (forceReload) {
      return this.loadTimeSeriesData().pipe(catchError(() => of([])));
    }
    return from(this.indexedDBService.getAll<TimeSeries>('timeSeries')).pipe(
      switchMap((timeSeries) => {
        if (!timeSeries.length) {
          return this.loadTimeSeriesData().pipe(catchError(() => of([])));
        }
        return of(timeSeries);
      })
    );
  }

  loadPriceCategories(forceReload: boolean = false) {
    const loadPriceCategories$ = this.httpClient.get<PriceCategory[]>(window.api_url + '/prices/categories/list').pipe(
      tap((priceCategories) => {
        this.indexedDBService.saveMany('priceCategories', priceCategories);
      })
    );
    if (forceReload) {
      return loadPriceCategories$;
    }
    return from(this.indexedDBService.getAll<PriceCategory>('priceCategories')).pipe(
      switchMap((priceCategories) => {
        if (!priceCategories.length) {
          return loadPriceCategories$;
        }
        return of(priceCategories);
      })
    );
  }
  loadMarketAreas(forceReload: boolean = false) {
    const loadMarketAreas$ = this.httpClient.get<MarketArea[]>(window.api_url + '/market_areas').pipe(
      tap((marketAreas) => {
        this.indexedDBService.saveMany('marketAreas', marketAreas);
      })
    );
    if (forceReload) {
      return loadMarketAreas$;
    }
    return from(this.indexedDBService.getAll<MarketArea>('marketAreas')).pipe(
      switchMap((marketAreas) => {
        if (!marketAreas.length) {
          return loadMarketAreas$;
        }
        return of(marketAreas);
      })
    );
  }
}
